import React, { useCallback, useState } from 'react';

interface FileDropzoneProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number;
  label?: string;
}

export function FileDropzone({
  onFileSelect,
  accept = '.xlsx,.xls',
  maxSize = 50,
  label = 'Arrastra tu archivo Excel aquí'
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const validateFile = (file: File): boolean => {
    setError(null);

    const validTypes = accept.split(',').map(t => t.trim());
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!validTypes.includes(fileExt) && !validTypes.includes(file.type)) {
      setError(`Formato no soportado. Usa: ${accept}`);
      return false;
    }

    if (file.size > maxSize * 1024 * 1024) {
      setError(`El archivo excede el límite de ${maxSize}MB`);
      return false;
    }

    return true;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];
    if (validateFile(file)) {
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (validateFile(file)) {
      onFileSelect(file);
    }
    e.target.value = '';
  }, [onFileSelect]);

  const dropzoneClass = `dropzone ${isDragging ? 'dragging' : ''} ${error ? 'error' : ''}`;

  return (
    <div>
      <div
        className={dropzoneClass}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => document.getElementById('etl-file-input')?.click()}
      >
        <span className="dropzone-icon">
          {isDragging ? '📤' : error ? '❌' : '📁'}
        </span>
        <p className="dropzone-title">
          {error || label}
        </p>
        <p className="dropzone-subtitle">
          o haz clic para seleccionar un archivo
        </p>
        <p className="dropzone-hint">
          Formatos: {accept} • Máximo: {maxSize}MB
        </p>
        <input
          id="etl-file-input"
          type="file"
          accept={accept}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
}